import { useSyncExternalStore } from 'react';
import {
  supabase,
  isSupabaseMock,
  getSupabaseMode,
  setSupabaseMode,
  onSupabaseModeChange,
  authRedirectUrl,
  initialAuthRedirect,
  type Session,
} from '../lib/supabase';
import { isEmailNotConfirmed, isInfraError, translateAuthError, type AuthErrorLike } from '../lib/authErrors';
import {
  PROFILE_LIMITS,
  SEAT_SEPARATOR,
  accountIdOf,
  sanitizeColor,
  sanitizeCountry,
  sanitizeFace,
  sanitizeDorsal,
  sanitizeUsername,
  type Player,
  type ProfilePatch,
  type Sport,
} from '../../../shared/types';
import { getSocket } from '../lib/socket';

/**
 * Autenticación + identidad del jugador, con **tolerancia total a fallos**.
 *
 * Es un store a nivel de módulo (no estado por componente): todas las
 * páginas comparten el mismo `player`. Antes cada página creaba su propio
 * invitado con un id aleatorio, por lo que el Hub creaba la sala con un id
 * y el Lobby se unía con otro → participantes duplicados "Invitado".
 *
 * Identidad:
 *  - Sesión Supabase (real o mock) → id y nombre de la cuenta.
 *  - Sin sesión → invitado con id estable **por pestaña** (sessionStorage):
 *    sobrevive a recargas (reconexión al lobby/partido).
 *  - Personalización (nombre, color, dorsal) persistida por id de jugador
 *    en localStorage y aplicada sobre la identidad base.
 *
 * Exclusividad por pestaña: un id de jugador solo puede estar activo en UNA
 * pestaña. Lo garantizamos con un Web Lock por id (se libera solo al cerrar
 * o recargar la pestaña). Sin esto, dos pestañas acababan siendo el mismo
 * jugador —la sesión de Supabase se comparte entre pestañas y "Duplicar
 * pestaña" copia el sessionStorage— y el lobby mostraba un único
 * participante que se "robaban" entre ellas. Si el id ya está ocupado:
 *  - invitado → se genera otro invitado para esta pestaña;
 *  - cuenta   → se usa un "asiento" `<id>~<tag>` propio de la pestaña.
 */
export interface AuthState {
  player: Player | null;
  loading: boolean;
  /** Último error de autenticación, ya traducido al español. */
  error: string | null;
  /** Aviso informativo (correo enviado, contraseña cambiada…). */
  notice: string | null;
  /** Email pendiente de confirmar: la UI ofrece "Reenviar correo". */
  unconfirmedEmail: string | null;
  /** Último cambio de Elo recibido del servidor tras un partido. */
  lastRating: RatingChange | null;
  /** `true` si la app está usando el mock local (no Supabase real). */
  isMock: boolean;
  /** `true` si el jugador es un invitado local (sin cuenta). */
  isGuest: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, username: string) => Promise<void>;
  resendConfirmation: (email: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  /** Cambia la contraseña de la sesión actual (enlace de recuperación). `true` si se cambió. */
  updatePassword: (password: string) => Promise<boolean>;
  clearMessages: () => void;
  signInAnonymously: () => Promise<Player | null>;
  signOut: () => Promise<void>;
  /** Actualiza nombre/color/dorsal localmente (persistente). Devuelve lo aplicado. */
  updateProfile: (patch: ProfilePatch) => ProfilePatch;
}

export interface RatingChange {
  sport: Sport;
  elo: number;
  delta: number;
  outcome: 'win' | 'loss' | 'draw';
  at: number;
}

interface Snapshot {
  player: Player | null;
  loading: boolean;
  error: string | null;
  notice: string | null;
  unconfirmedEmail: string | null;
  lastRating: RatingChange | null;
  isMock: boolean;
}

const SESSION_PROBE_TIMEOUT_MS = 2_500;
const GUEST_KEY = 'sportsarena.guest';
const GUEST_PREFIX = 'guest_';
const SEAT_KEY = 'sportsarena.seat';
/** Último id que usó esta pestaña (sobrevive a recargas y a la restauración tras apagar la pantalla). */
const LAST_ID_KEY = 'sportsarena.lastId';
/** Si esta pestaña ya era la cuenta, espera hasta esto a que su instancia anterior suelte el bloqueo. */
const RESUME_CLAIM_MS = 4000;
const PROFILE_KEY_PREFIX = 'sportsarena.profile.';
const LOCK_PREFIX = 'sportsarena.player.';
/** Espera al reclamar un id ocupado: en una recarga la página vieja aún puede tenerlo. */
const CLAIM_RETRY_MS = 600;
const DEFAULT_COLOR = '#22d3ee';

// ─────────────────────────────────────────────
// Persistencia (siempre tolerante: modo privado, cuota llena…)
// ─────────────────────────────────────────────
const readJson = <T,>(storage: Storage | undefined, key: string): T | null => {
  try {
    const raw = storage?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};
const writeJson = (storage: Storage | undefined, key: string, value: unknown): void => {
  try {
    storage?.setItem(key, JSON.stringify(value));
  } catch {
    /* almacenamiento no disponible → se ignora */
  }
};
const local = typeof window !== 'undefined' ? window.localStorage : undefined;
const session = typeof window !== 'undefined' ? window.sessionStorage : undefined;

const randomId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

/** Sufijo corto y legible derivado del id (p. ej. "Invitado-7F3A"). */
const shortTag = (id: string): string =>
  id.replace(/[^a-zA-Z0-9]/g, '').slice(-4).toUpperCase().padStart(4, '0');

/**
 * Personalización guardada en este navegador. Se guarda por CUENTA (sin el
 * sufijo de asiento `~TAG`): antes se guardaba por id de pestaña y, si la
 * pestaña usaba un asiento, al volver a entrar se perdía (bug del país).
 */
const profileKey = (playerId: string): string => PROFILE_KEY_PREFIX + accountIdOf(playerId);
const readProfile = (playerId: string): ProfilePatch => ({
  ...(readJson<ProfilePatch>(local, PROFILE_KEY_PREFIX + playerId) ?? {}),
  ...(readJson<ProfilePatch>(local, profileKey(playerId)) ?? {}),
});
const writeProfile = (playerId: string, patch: ProfilePatch): void =>
  writeJson(local, profileKey(playerId), { ...readProfile(playerId), ...patch });

/** Construye el `Player` aplicando la personalización guardada. */
const buildPlayer = (id: string, baseUsername: string): Player => {
  const profile = readProfile(id);
  const now = new Date().toISOString();
  return {
    id,
    username: sanitizeUsername(profile.username) ?? baseUsername,
    avatarConfig: {
      color: sanitizeColor(profile.color) ?? DEFAULT_COLOR,
      dorsal: sanitizeDorsal(profile.dorsal) ?? undefined,
      country: sanitizeCountry(profile.country),
      face: sanitizeFace(profile.face),
      avatarUrl: null,
      accessory: null,
      build: 'athletic',
      athleteVisual: {
        characterModel: {
          id: 'default_athlete',
          name: 'Default Athlete',
          modelUrl: '',
          lod: 'medium',
          textures: [],
          animations: [],
          sizeKB: 0,
          format: 'glb',
        },
        jerseyTexture: { type: 'diffuse', url: '', resolution: '1k' },
        equipment: [],
        animations: [],
        skinTone: '#d4a373',
      },
      preferredRenderQuality: {
        preset: 'auto',
        antialiasing: true,
        shadowQuality: 'medium',
        textureQuality: 'high',
        fpsTarget: 60,
        bloom: true,
        motionBlur: false,
      },
    },
    stats: {
      gamesPlayed: 0,
      wins: 0,
      losses: 0,
      draws: 0,
      goals: 0,
      elo: { futbol: 1000, beisbol: 1000, basquetbol: 1000 },
    },
    createdAt: now,
    lastSeen: now,
  };
};

// ─────────────────────────────────────────────
// Reclamo exclusivo de ids por pestaña (Web Locks)
// ─────────────────────────────────────────────
const heldClaims = new Map<string, () => void>();

const tryLock = (id: string): Promise<boolean> =>
  new Promise((resolve) => {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    // Sin Web Locks (navegador antiguo / contexto no seguro): no se puede comprobar.
    if (!locks) {
      resolve(true);
      return;
    }
    let release: () => void = () => undefined;
    const held = new Promise<void>((r) => {
      release = r;
    });
    locks
      .request(LOCK_PREFIX + id, { ifAvailable: true }, async (lock) => {
        if (!lock) {
          resolve(false);
          return;
        }
        heldClaims.set(id, release);
        resolve(true);
        await held; // se mantiene hasta que la pestaña cambie de identidad o se cierre
      })
      .catch(() => resolve(true));
  });

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Reclama `id` para esta pestaña. `false` si otra pestaña viva lo usa. */
const inFlightClaims = new Map<string, Promise<boolean>>();
const claim = (id: string): Promise<boolean> => {
  if (heldClaims.has(id)) return Promise.resolve(true);
  // Dos reclamos simultáneos del mismo id en esta pestaña comparten resultado
  // (si no, el segundo vería el lock "ocupado" por el primero).
  let pending = inFlightClaims.get(id);
  if (!pending) {
    pending = (async () => {
      if (await tryLock(id)) return true;
      await wait(CLAIM_RETRY_MS);
      return tryLock(id);
    })().finally(() => inFlightClaims.delete(id));
    inFlightClaims.set(id, pending);
  }
  return pending;
};

/** Libera todos los ids reclamados salvo `keep`. */
const releaseClaimsExcept = (keep: string): void => {
  heldClaims.forEach((release, id) => {
    if (id === keep) return;
    release();
    heldClaims.delete(id);
  });
};

const withSuffix = (base: string, tag: string): string =>
  `${base.slice(0, PROFILE_LIMITS.usernameMax - tag.length - 1)}-${tag}`;

/** Jugador a partir de una sesión de Supabase (real o mock), exclusivo de esta pestaña. */
const playerFromSession = async (s: Session): Promise<Player> => {
  const u = s.user;
  const metaName = sanitizeUsername(u.user_metadata?.username);
  const emailName = sanitizeUsername(u.email?.split('@')[0]);
  const base =
    (metaName && metaName.toLowerCase() !== 'invitado' ? metaName : null) ??
    emailName ??
    `Invitado-${shortTag(u.id)}`;
  if (await claim(u.id)) return buildPlayer(u.id, base);
  // Esta pestaña ya era la cuenta (recarga, o restaurada tras apagarse la pantalla):
  // su instancia anterior aún puede tener el bloqueo unos instantes; se espera
  // en vez de crear un asiento nuevo con sufijo.
  if (readJson<{ id: string }>(session, LAST_ID_KEY)?.id === u.id) {
    const until = Date.now() + RESUME_CLAIM_MS;
    while (Date.now() < until) {
      await wait(CLAIM_RETRY_MS);
      if (await tryLock(u.id)) return buildPlayer(u.id, base);
    }
  }

  // La cuenta ya juega en otra pestaña: asiento propio para esta.
  let seat = readJson<{ accountId: string; tag: string }>(session, SEAT_KEY);
  if (!seat || seat.accountId !== u.id || !(await claim(`${u.id}${SEAT_SEPARATOR}${seat.tag}`))) {
    seat = { accountId: u.id, tag: randomId().slice(0, 4).toUpperCase() };
    writeJson(session, SEAT_KEY, seat);
    await claim(`${u.id}${SEAT_SEPARATOR}${seat.tag}`);
  }
  return buildPlayer(`${u.id}${SEAT_SEPARATOR}${seat.tag}`, withSuffix(base, seat.tag));
};

/** Invitado local con id estable por pestaña y exclusivo de ella. */
const guestPlayer = async (): Promise<Player> => {
  let guest = readJson<{ id: string }>(session, GUEST_KEY);
  // Sin invitado, o su id lo usa otra pestaña viva (p. ej. "Duplicar pestaña").
  if (!guest?.id || !(await claim(guest.id))) {
    guest = { id: `${GUEST_PREFIX}${randomId()}` };
    writeJson(session, GUEST_KEY, guest);
    await claim(guest.id);
  }
  return buildPlayer(guest.id, `Invitado-${shortTag(guest.id)}`);
};

// ─────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────
let snapshot: Snapshot = {
  player: null,
  loading: true,
  error: null,
  notice: null,
  unconfirmedEmail: null,
  lastRating: null,
  isMock: isSupabaseMock,
};
const listeners = new Set<() => void>();

const setSnapshot = (patch: Partial<Snapshot>): void => {
  snapshot = { ...snapshot, ...patch };
  listeners.forEach((l) => l());
};

/**
 * Fija el jugador de la pestaña. Las resoluciones son asíncronas (Web
 * Locks): solo se aplica la más reciente.
 */
let identitySeq = 0;
const applyIdentity = async (resolve: () => Promise<Player>): Promise<Player | null> => {
  const seq = ++identitySeq;
  const player = await resolve();
  if (seq !== identitySeq) return null;
  releaseClaimsExcept(player.id);
  writeJson(session, LAST_ID_KEY, { id: player.id });
  setSnapshot({ player, loading: false });
  syncSocketIdentity(player);
  void loadRemoteProfile(player);
  return player;
};

// ─────────────────────────────────────────────
// Perfil en la cuenta (tabla `profiles`, RLS: cada uno su fila)
// ─────────────────────────────────────────────
interface ProfileRow {
  username: string;
  color: string;
  dorsal: number | null;
  elo: Partial<Record<Sport, number>> | null;
  games_played: number;
  wins: number;
  losses: number;
  draws: number;
}

const usesRemoteProfile = (player: Player | null): player is Player =>
  !!player && !isGuestPlayer(player) && getSupabaseMode() === 'real';

const seatTagOf = (playerId: string): string | null => playerId.split(SEAT_SEPARATOR)[1] ?? null;

/** Fusiona la fila de `profiles` en el jugador (el asiento de pestaña conserva su sufijo). */
const mergeProfile = (player: Player, row: ProfileRow, country: string | null | undefined): Player => {
  const tag = seatTagOf(player.id);
  const name = sanitizeUsername(row.username) ?? player.username;
  return {
    ...player,
    username: tag ? withSuffix(name, tag) : name,
    avatarConfig: {
      ...player.avatarConfig,
      color: sanitizeColor(row.color) ?? player.avatarConfig.color,
      dorsal: sanitizeDorsal(row.dorsal) ?? player.avatarConfig.dorsal,
      // El país vive en los metadatos del usuario (no hay columna en `profiles`).
      country: country === undefined ? player.avatarConfig.country : sanitizeCountry(country),
    },
    stats: {
      ...player.stats,
      gamesPlayed: row.games_played,
      wins: row.wins,
      losses: row.losses,
      draws: row.draws,
      elo: { ...player.stats.elo, ...(row.elo ?? {}) },
    },
  };
};

/**
 * Carga el perfil guardado en la cuenta. Si la cuenta no tiene fila (cuenta
 * anterior a la tabla), la crea con la identidad actual.
 */
const loadRemoteProfile = async (player: Player): Promise<void> => {
  if (isGuestPlayer(player)) return;
  // País de la cuenta (metadatos de Auth: también con el mock local).
  const country = await remoteCountry();
  if (country !== undefined && snapshot.player?.id === player.id && (snapshot.player.avatarConfig.country ?? null) !== country) {
    const cur = snapshot.player;
    writeProfile(cur.id, { country });
    setSnapshot({ player: { ...cur, avatarConfig: { ...cur.avatarConfig, country } } });
    syncCountryToRoom(country);
  }
  // Rostro de la cuenta (mismos metadatos que el país).
  const face = await remoteFace();
  if (face !== undefined && snapshot.player?.id === player.id && (snapshot.player.avatarConfig.face ?? null) !== face) {
    const cur = snapshot.player;
    writeProfile(cur.id, { face });
    setSnapshot({ player: { ...cur, avatarConfig: { ...cur.avatarConfig, face } } });
    syncFaceToRoom(face);
  }
  if (!usesRemoteProfile(player)) return;
  const accountId = accountIdOf(player.id);
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('username, color, dorsal, elo, games_played, wins, losses, draws')
      .eq('id', accountId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) {
      const base = seatTagOf(player.id) ? player.username.replace(/-[A-Z0-9]{4}$/, '') : player.username;
      await supabase.from('profiles').insert({
        id: accountId,
        username: base,
        color: player.avatarConfig.color,
        dorsal: player.avatarConfig.dorsal ?? null,
      });
      return;
    }
    const current = snapshot.player;
    if (current?.id !== player.id) return; // cambió de identidad mientras tanto
    const merged = mergeProfile(current, data as ProfileRow, undefined);
    if ((merged.avatarConfig.country ?? null) !== (current.avatarConfig.country ?? null)) {
      writeProfile(current.id, { country: merged.avatarConfig.country ?? null });
      syncCountryToRoom(merged.avatarConfig.country ?? null);
    }
    setSnapshot({ player: merged });
  } catch (err) {
    console.warn('[perfil] No se pudo cargar el perfil de la cuenta:', (err as Error).message);
  }
};

/**
 * País guardado en la cuenta: metadatos del usuario de Supabase Auth
 * (`user_metadata.country`), sin cambiar el esquema de `profiles`.
 * `"custom"` = eligió color personalizado. `undefined` = la cuenta aún no
 * eligió (se respeta lo del navegador). Se lee del servidor (`getUser`), no de
 * la sesión guardada, que puede ser de antes del último cambio.
 */
const CUSTOM_COLOR = 'custom';
const remoteCountry = async (): Promise<string | null | undefined> => {
  try {
    const { data } = await supabase.auth.getUser();
    const meta = (data.user?.user_metadata ?? {}) as { country?: unknown };
    return 'country' in meta && meta.country !== null ? sanitizeCountry(meta.country) : undefined;
  } catch {
    return undefined;
  }
};

/** Guarda el país en la cuenta. Nunca envía null (Supabase borraría la clave): "custom". */
const saveRemoteCountry = (player: Player, country: string | null): void => {
  if (isGuestPlayer(player)) return;
  void (async () => {
    try {
      const { error } = await supabase.auth.updateUser({ data: { country: country ?? CUSTOM_COLOR } });
      if (error) console.warn('[perfil] No se pudo guardar el país:', error.message);
    } catch (err) {
      console.warn('[perfil] No se pudo guardar el país:', (err as Error).message);
    }
  })();
};

/** Rostro guardado en la cuenta (`user_metadata.face`); `"auto"` = el automático; `undefined` = sin elegir. */
const AUTO_FACE = 'auto';
const remoteFace = async (): Promise<string | null | undefined> => {
  try {
    const { data } = await supabase.auth.getUser();
    const meta = (data.user?.user_metadata ?? {}) as { face?: unknown };
    if (!('face' in meta) || meta.face === null) return undefined;
    return meta.face === AUTO_FACE ? null : sanitizeFace(meta.face);
  } catch {
    return undefined;
  }
};
const saveRemoteFace = (player: Player, face: string | null): void => {
  if (isGuestPlayer(player)) return;
  void (async () => {
    try {
      const { error } = await supabase.auth.updateUser({ data: { face: face ?? AUTO_FACE } });
      if (error) console.warn('[perfil] No se pudo guardar el rostro:', error.message);
    } catch (err) {
      console.warn('[perfil] No se pudo guardar el rostro:', (err as Error).message);
    }
  })();
};
const syncFaceToRoom = (face: string | null): void => {
  const roomId = currentRoomId();
  if (!roomId) return;
  void getSocket().then((socket) => socket.emit('lobby:updateProfile', { roomId, face }));
};

/** Sala actual según la URL (/lobby/:id o /match/:id). */
const currentRoomId = (): string | null =>
  typeof window === 'undefined' ? null : /\/(?:lobby|match)\/([^/?#]+)/.exec(window.location.pathname + window.location.hash)?.[1] ?? null;

/**
 * El país llegó de la cuenta después de entrar en la sala: se avisa al
 * servidor para que el uniforme y la bandera del marcador lo usen.
 */
const syncCountryToRoom = (country: string | null): void => {
  const roomId = currentRoomId();
  if (!roomId) return;
  void getSocket().then((socket) => socket.emit('lobby:updateProfile', { roomId, country }));
};

/** Guarda nombre/color/dorsal en la cuenta (sin bloquear la UI). */
const saveRemoteProfile = (player: Player, applied: ProfilePatch): void => {
  if (!usesRemoteProfile(player)) return;

  const row: Partial<Pick<ProfileRow, 'username' | 'color' | 'dorsal'>> = {};
  if (applied.username) row.username = applied.username;
  if (applied.color) row.color = applied.color;
  if (applied.dorsal !== undefined) row.dorsal = applied.dorsal;
  if (Object.keys(row).length === 0) return;
  void (async () => {
    const { error } = await supabase.from('profiles').update(row).eq('id', accountIdOf(player.id));
    if (error) console.warn('[perfil] No se pudo guardar el perfil:', error.message);
  })();
};

// ─────────────────────────────────────────────
// Socket: el servidor valida la cuenta con el token del handshake, así
// que al entrar o salir de una cuenta hay que reconectar con el token nuevo.
// ─────────────────────────────────────────────
let socketIdentity: string | null = null;
let ratingListenerReady = false;

const syncSocketIdentity = (player: Player | null): void => {
  const identity = player && !isGuestPlayer(player) ? accountIdOf(player.id) : 'guest';
  const previous = socketIdentity;
  socketIdentity = identity;
  void getSocket().then((socket) => {
    if (!ratingListenerReady) {
      ratingListenerReady = true;
      socket.on('profile:updated', (u) => {
        const current = snapshot.player;
        if (!current || accountIdOf(current.id) !== u.accountId) return;
        setSnapshot({
          lastRating: { sport: u.sport, elo: u.elo, delta: u.delta, outcome: u.outcome, at: Date.now() },
          player: {
            ...current,
            stats: { ...current.stats, elo: { ...current.stats.elo, [u.sport]: u.elo } },
          },
        });
        void loadRemoteProfile(current);
      });
    }
    // Primera identidad: el socket ya se conectó con el token de esta sesión.
    if (previous !== null && previous !== identity) {
      socket.disconnect();
      socket.connect();
    }
  });
};

let initialized = false;
let unsubscribeAuth: (() => void) | null = null;
/** `true` mientras ESTA pestaña inicia sesión (las demás no deben cambiar de jugador). */
let localSignInPending = false;

const subscribeAuthChanges = (): void => {
  unsubscribeAuth?.();
  try {
    const { data } = supabase.auth.onAuthStateChange((event, s) => {
      if (!s?.user) return; // Sin sesión: conservamos el invitado; solo `signOut` lo limpia.
      const current = snapshot.player;
      // Un inicio de sesión hecho en OTRA pestaña (Supabase sincroniza la
      // sesión por localStorage) no convierte a este invitado en esa cuenta.
      if (event === 'SIGNED_IN' && isGuestPlayer(current) && !localSignInPending) return;
      // Renovación de token del mismo jugador: nada que cambiar.
      if (current && accountIdOf(current.id) === s.user.id) return;
      // Antes de la sesión inicial lo resuelve `initStore`.
      if (!current && !localSignInPending) return;
      const sessionUser = s;
      void applyIdentity(() => playerFromSession(sessionUser));
    });
    unsubscribeAuth = () => data.subscription.unsubscribe();
  } catch (err) {
    console.warn('[auth] No se pudo suscribir a cambios de sesión:', err);
  }
};

const withTimeout = async <T,>(promise: Promise<T>, ms: number): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`timeout ${ms}ms`)), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
};

const initStore = (): void => {
  if (initialized) return;
  initialized = true;

  onSupabaseModeChange((mode) => {
    setSnapshot({ isMock: mode === 'mock' });
    // El cliente activo cambió: re-suscribimos sus eventos de auth.
    subscribeAuthChanges();
  });
  subscribeAuthChanges();

  void (async () => {
    let current: Session | null = null;
    try {
      // Volviendo de un enlace de correo, Supabase valida el token con la red: más margen.
      const timeout = initialAuthRedirect.type ? SESSION_PROBE_TIMEOUT_MS * 4 : SESSION_PROBE_TIMEOUT_MS;
      const { data } = await withTimeout(supabase.auth.getSession(), timeout);
      current = data.session?.user ? data.session : null;
    } catch (err) {
      console.warn('[auth] Probe inicial falló, activando mock local:', err);
      setSupabaseMode('mock', 'session_probe_failed');
    }
    if (snapshot.player) return;
    // Sin sesión → invitado local: la UI nunca queda bloqueada.
    await applyIdentity(() => (current ? playerFromSession(current) : guestPlayer()));
  })();
};

const subscribe = (listener: () => void): (() => void) => {
  initStore();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const getSnapshot = (): Snapshot => snapshot;

// ─────────────────────────────────────────────
// Acciones
// ─────────────────────────────────────────────
type AuthResult = {
  data: { user?: { identities?: unknown[] | null } | null; session?: unknown } | null;
  error: AuthErrorLike | null;
};

/**
 * Ejecuta contra el cliente activo. Solo ante un fallo de infraestructura
 * real (sin red, 5xx) reintenta en el mock local; los 4xx se muestran.
 */
const runAuth = async (call: () => Promise<AuthResult>, allowMockFallback = true): Promise<AuthResult> => {
  setSnapshot({ loading: true, error: null, notice: null });
  let result: AuthResult;
  try {
    result = await call();
  } catch (err) {
    result = { data: null, error: { name: 'AuthRetryableFetchError', message: String(err) } };
  }
  if (allowMockFallback && result.error && isInfraError(result.error) && getSupabaseMode() !== 'mock') {
    setSupabaseMode('mock', 'auth_call_failed');
    try {
      result = await call();
    } catch (err) {
      result = { data: null, error: { message: String(err) } };
    }
  }
  setSnapshot({ loading: false, error: translateAuthError(result.error) });
  return result;
};

/** Marca el inicio de sesión como propio de esta pestaña mientras dura. */
const localSignIn = async (call: () => Promise<AuthResult>): Promise<AuthResult> => {
  localSignInPending = true;
  try {
    return await runAuth(call);
  } finally {
    // Supabase emite SIGNED_IN antes de resolver o en el siguiente tick.
    setTimeout(() => {
      localSignInPending = false;
    }, 0);
  }
};

const signInWithEmail = async (email: string, password: string): Promise<void> => {
  const result = await localSignIn(
    () => supabase.auth.signInWithPassword({ email, password }) as unknown as Promise<AuthResult>
  );
  setSnapshot({ unconfirmedEmail: isEmailNotConfirmed(result.error) ? email : null });
};

const signUpWithEmail = async (email: string, password: string, username: string): Promise<void> => {
  const result = await localSignIn(
    () =>
      supabase.auth.signUp({
        email,
        password,
        options: { data: { username }, emailRedirectTo: authRedirectUrl('/login') },
      }) as unknown as Promise<AuthResult>
  );
  if (result.error || result.data?.session) return; // error ya traducido, o entró directo
  const user = result.data?.user;
  if (user && Array.isArray(user.identities) && user.identities.length === 0) {
    // Supabase no revela si el correo existe: devuelve un usuario sin identidades.
    setSnapshot({ error: translateAuthError({ message: 'already registered', code: 'user_already_exists' }) });
    return;
  }
  setSnapshot({
    unconfirmedEmail: email,
    notice: `Te enviamos un correo a ${email}, confírmalo para entrar. Si no llega en unos minutos, revisa la carpeta de spam.`,
  });
};

const resendConfirmation = async (email: string): Promise<void> => {
  const result = await runAuth(
    () =>
      supabase.auth.resend({
        type: 'signup',
        email,
        options: { emailRedirectTo: authRedirectUrl('/login') },
      }) as unknown as Promise<AuthResult>,
    false
  );
  if (!result.error) setSnapshot({ notice: `Te reenviamos el correo de confirmación a ${email}.` });
};

const requestPasswordReset = async (email: string): Promise<void> => {
  const result = await runAuth(
    () =>
      supabase.auth.resetPasswordForEmail(email, {
        redirectTo: authRedirectUrl('/reset'),
      }) as unknown as Promise<AuthResult>,
    false
  );
  if (!result.error) {
    setSnapshot({
      notice: `Si existe una cuenta con ${email}, te enviamos un enlace para crear una contraseña nueva.`,
    });
  }
};

const updatePassword = async (password: string): Promise<boolean> => {
  const result = await runAuth(
    () => supabase.auth.updateUser({ password }) as unknown as Promise<AuthResult>,
    false
  );
  if (result.error) return false;
  setSnapshot({ notice: 'Contraseña actualizada. Ya puedes jugar con tu cuenta.' });
  return true;
};

const clearMessages = (): void => setSnapshot({ error: null, notice: null, unconfirmedEmail: null });

const signInAnonymously = async (): Promise<Player | null> => {
  if (snapshot.player) return snapshot.player;
  // Invitado local: identidad por pestaña, sin depender de la red.
  return applyIdentity(guestPlayer);
};

const signOut = async (): Promise<void> => {
  try {
    await supabase.auth.signOut();
  } catch (err) {
    console.warn('[auth] signOut lanzó excepción, ignorando:', err);
  }
  try {
    session?.removeItem(GUEST_KEY);
    session?.removeItem(SEAT_KEY);
  } catch {
    /* ignore */
  }
  identitySeq += 1;
  releaseClaimsExcept('');
  setSnapshot({ player: null, lastRating: null });
  syncSocketIdentity(null);
};

const updateProfile = (patch: ProfilePatch): ProfilePatch => {
  const current = snapshot.player;
  if (!current) return {};
  const applied: ProfilePatch = {};
  const username = patch.username !== undefined ? sanitizeUsername(patch.username) : null;
  const color = patch.color !== undefined ? sanitizeColor(patch.color) : null;
  const dorsal = patch.dorsal !== undefined ? sanitizeDorsal(patch.dorsal) : null;
  if (username) applied.username = username;
  if (color) applied.color = color;
  if (dorsal !== null) applied.dorsal = dorsal;
  // País: código válido, o null para volver al color personalizado.
  if (patch.country === null) applied.country = null;
  else if (patch.country !== undefined && sanitizeCountry(patch.country)) applied.country = sanitizeCountry(patch.country);
  if (patch.face === null) applied.face = null;
  else if (patch.face !== undefined && sanitizeFace(patch.face)) applied.face = sanitizeFace(patch.face);
  if (Object.keys(applied).length === 0) return applied;

  writeProfile(current.id, applied);
  saveRemoteProfile(current, applied);
  if (applied.country !== undefined) saveRemoteCountry(current, applied.country);
  if (applied.face !== undefined) saveRemoteFace(current, applied.face);
  setSnapshot({
    player: {
      ...current,
      username: applied.username ?? current.username,
      avatarConfig: {
        ...current.avatarConfig,
        color: applied.color ?? current.avatarConfig.color,
        dorsal: applied.dorsal ?? current.avatarConfig.dorsal,
        country: applied.country !== undefined ? applied.country : current.avatarConfig.country ?? null,
        face: applied.face !== undefined ? applied.face : current.avatarConfig.face ?? null,
      },
    },
  });
  return applied;
};

const actions = {
  signInWithEmail,
  signUpWithEmail,
  resendConfirmation,
  requestPasswordReset,
  updatePassword,
  clearMessages,
  signInAnonymously,
  signOut,
  updateProfile,
};

/** `true` si el jugador es un invitado local sin cuenta. */
/**
 * El servidor devolvió a esta pestaña su asiento original de la misma cuenta
 * (volvió como otra instancia tras apagarse la pantalla): se adopta ese id y
 * su nombre, sin sufijo, y se olvida el asiento temporal.
 */
export const adoptIdentity = (id: string): void => {
  const current = snapshot.player;
  if (!current || current.id === id) return;
  identitySeq += 1; // cualquier resolución en curso queda obsoleta
  const base = current.username.replace(/-[A-Z0-9]{4}$/, '');
  const player: Player = { ...buildPlayer(id, base), stats: current.stats };
  session?.removeItem(SEAT_KEY);
  writeJson(session, LAST_ID_KEY, { id });
  releaseClaimsExcept('');
  void claim(id);
  setSnapshot({ player });
  syncSocketIdentity(player);
};

export const isGuestPlayer = (player: Player | null): boolean =>
  !!player && player.id.startsWith(GUEST_PREFIX);


export const useSupabaseAuth = (): AuthState => {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { ...state, isGuest: isGuestPlayer(state.player), ...actions };
};
